<template>
    <div>
        <div v-if="loading && !invalidResults">
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </div>
        <div v-else v-for="(matches, date) in groupedMatches" :key="date">
            <v-row class="text-uppercase font-weight-bold mt-2">
                <v-col cols="3" class="font-weight-bold header-row">{{ date }}</v-col>

                <v-col v-if="$vuetify.breakpoint.width > 400" cols="9">
                    <v-row v-if="someMatchesWithTwoCompetitors(matches)">
                        <v-col cols="3" class="text-right text--disabled">{{ $translate('TEAMSPORT_HOME') }}</v-col>
                        <v-col cols="2" />
                        <v-col cols="7" class="text-left text--disabled">{{ $translate('TEAMSPORT_AWAY') }}</v-col>
                    </v-row>
                </v-col>
            </v-row>
            <hr class="mb-4" />
            <v-row
                v-for="match in matches"
                :key="match.id"
                class="align-center justify-center"
                :class="{ 'z-clickable': !referee }"
                @click.prevent="!referee ? gotoMatch(match) : null"
            >
                <v-col cols="12">
                    <v-card>
                        <v-card-text>
                            <div class="d-flex align-center mb-3" style="font-size: 18px;">
                                <div v-if="match.startTime">
                                    <v-icon class="mr-3" size="24">mdi-clock</v-icon>
                                    <span class="font-weight-bold">{{ match.startTime.hhmm() }}</span>
                                </div>
                                <v-spacer></v-spacer>
                                <div v-if="match.location">
                                    <v-icon class="mr-3" size="24">mdi-map-marker</v-icon>
                                    <span  class="font-weight-bold">{{ match.location }}</span>
                                </div>
                            </div>
                            <div v-if="someMatchesWithTwoCompetitors(matches)" class="result-container" style="font-size: 20px;">
                                <div class="team team-left">
                                    <span class="team-name">{{ match.competitors[0].name }}</span>
                                </div>
                                <div class="match-container" style="display: flex; align-items: center; justify-content: center;">
                                    <v-avatar v-if="match.competitors[0].team_id" size="60" class="team-avatar ml-8">
                                        <v-img :src="getTeamImageUrl(match.competitors[0].team_id)"></v-img>
                                    </v-avatar>
                                    <div class="match-result" style="margin-left: 15%; margin-right: 15%;">
                                        <span class="font-weight-bold">{{ getResult(match) }}</span>
                                    </div>
                                    <v-avatar v-if="match.competitors[1].team_id" size="60" class="team-avatar mr-8">
                                        <v-img :src="getTeamImageUrl(match.competitors[1].team_id)"></v-img>
                                    </v-avatar>
                                </div>
                                <div class="team team-right" style="display: flex; justify-content: center;">
                                    <span class="team-name">{{ match.competitors[1].name }}</span>
                                </div>
                            </div>
                            <div v-else class="d-flex justify-center align-center" style="font-size: 20px;">
                                <span class="team-name">{{ match.name }}</span>
                            </div>
                        </v-card-text>
                        <v-card-actions v-if="referee">
                            <v-btn outlined @click="gotoMatch(match)"> {{ $translate('Go to match') }} </v-btn>
                            <v-btn outlined v-if="canReportResult(match)" @click="setResult(match)">{{ $translate('Set result') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </div>
        <TeamsportSetResultDialog v-model="showSetResultDialog" :match="selectedMatch" v-if="showSetResultDialog" />
    </div>
</template>

<script>
import TeamsportSetResultDialog from './TeamsportSetResultDialog.vue';
export default {
    name: 'zoezi-teamsport-match-list',
    
    props: {
        matches: {
            type: Array
        },
        showResult: {
            type: Boolean
        },
        referee: {
            type: Boolean,
            default: false
        }
    },

    components: {
        TeamsportSetResultDialog
    },

    data() {
        return {
            showSetResultDialog: false,
            selectedMatch: null,
            loading: false,
            unsubscribe: [],
            invalidResults: null,
            settings: null
        }
    },
    
    computed: {
        groupedMatches() {
            return this.matches.reduce((grouped, match) => {
                let dateKey = match.startTime ? Date.formatFullShort(match.startTime) : this.$translate('Date not set');
                (grouped[dateKey] = grouped[dateKey] || []).push(match);
                return grouped;
            }, {});
        },
    },
        
    methods: {
        fetchTeamsportSettings() {
            this.$api.get('/api/teamsport/settings/get').then(settings => {
                this.settings = settings;
            })
        },
        getInvaldResults() {
            this.loading = true;
            this.$api.get('/api/teamsport/resultinvalid/get').then(response => {
                this.invalidResults = response;
                this.loading = false;
            });
        },
        canReportResult(match) {
            if (!this.$store.state.user) {
                return false;
            }

            if (!this.settings) {
                return false;
            }

            const isReferee = match.referees_id && match.referees_id.includes(this.$store.state.user.id);
            const timeOk = match.activity && this.$store.state.now30s < Date.newFull(match.activity.endTime).addDays(this.settings?.result_report_days);
            const refereeCanReport = ['both', 'result'].includes(this.settings?.referee_report);
            return (isReferee && refereeCanReport) || (!match.finished || timeOk);
        },
        getTeamImageUrl(teamId) {
            return '/api/public/image/get?size=50x50&type=team&id=' + teamId;
        },
        getResult(match) {
            if (match.result_type === 'padel') {
                return match.result?.[0]?.result ?? '-';
            }
            const getTeamResult = (teamId) => {
                const resultObject = match.result?.find(r => r.team_id === teamId);
                if (resultObject) {
                    if (resultObject.invalid_id) {
                        const invalidResult = this.invalidResults?.find(r => r.id === resultObject.invalid_id);
                        return invalidResult ? invalidResult.name : '0';
                    }
                    return resultObject.result || ' ';
                }
                return ' ';
            };
            const [homeTeamId, awayTeamId] = match.competitors?.map(competitor => competitor.team_id);
            const homeResult = getTeamResult(homeTeamId);
            const awayResult = getTeamResult(awayTeamId);
            let result = `${homeResult} - ${awayResult}`;

            if (!result || result.trim() === '') {
                return '&nbsp;-&nbsp;';
            }
            return result;
        },
        gotoMatch(match) {
            const matchPage = this.$root.siteconfig?.pages?.match;
            this.$root.followLink(matchPage, match.id);
        },
        someMatchesWithTwoCompetitors(matches) {
            return matches.some(match => match.competitors?.length == 2);
        },
        setResult(match) {
            this.selectedMatch = match;
            this.showSetResultDialog = true;
        }
    },

    mounted() {
        this.getInvaldResults();
        this.unsubscribe.push(this.$api.subscribe({
            type: 'teamsportresultinvalid',
            callback: () => this.getInvaldResults()
        }));
        this.fetchTeamsportSettings();
    },

    destroyed() {
        this.unsubscribe.forEach(unsubscribe => unsubscribe());
    }
}
</script>
<style scoped>
.result-container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
    justify-items: center;
    text-align: center;
}



.team-name {
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 250px;
}

.team-avatar {
    width: 60px;
    height: 60px;
    object-fit: cover;
}

.team {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.team-right {
    justify-content: flex-start;
}

.match-result {
    flex: 0 1 auto;
    min-width: 80px;
    justify-content: center;
    display: flex;
    align-items: center;
}

@media (max-width: 600px) {
    .team-avatar {
        display: none;
    }
    .team, .team-right {
        justify-content: center;
    }
    .team-name {
        font-size: 16px;
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
    }
}
.header-row {
    white-space: nowrap;
}

</style>
